import React from 'react'

import cx from 'classnames'
import { useTranslations } from 'next-intl'
import Head from 'next/head'
import Image from 'next/legacy/image'
import { useRouter } from 'next/router'

import { Button } from '@/components/buttons/Button'
import { Collapsible, CollapsibleItem } from '@/components/Collapsible'
import { donationSteps } from '@/components/donations/steps'
import { PageMetaTags } from '@/components/MetaTags'
import { Page } from '@/components/Page'
import { SimpleHero } from '@/components/SimpleHero'
import { Spacer } from '@/components/Spacer'
import { useLocale } from '@/features/locale/useLocale'
import { faqs, FAQSectionKey, FAQType } from '@/fixtures/faq'
import { formatFAQCopyKey } from '@/localization/utils'
import { getStaticPageProps } from '@/ssr/wrapper'

import styles from './help.module.scss'

export default function Help() {
  const router = useRouter()
  const t = useTranslations()
  const locale = useLocale()

  const currentFAQItem = faqs.find((f) => f.sectionKey === router.query.cat) || faqs[0]
  const faqItems: CollapsibleItem[] | undefined = currentFAQItem.ids.map((faqId) => ({
    title: t(formatFAQCopyKey(faqId, FAQType.Question)),
    content: t.rich(formatFAQCopyKey(faqId, FAQType.Answer)),
  }))

  const currentCategory = currentFAQItem.sectionKey

  const onCategoryChange = (key: FAQSectionKey) => {
    router.replace({ query: { cat: key } }, undefined, { shallow: true })
  }

  return (
    <Page>
      <PageMetaTags title={t('help_title')} description={t('help_description')} />
      <Head>
        <title>{t('route_help')}</title>
      </Head>
      <SimpleHero title={t('help_title')} subtitle={t('help_description')} />
      <div className={styles.Help}>
        <div className={styles.FAQ}>
          <ul>
            {faqs?.map((f) => (
              <li
                className={cx({ [styles.Active]: currentCategory === f.sectionKey })}
                key={f.sectionKey}
                onClick={() => onCategoryChange(f.sectionKey)}
              >
                {t(f.sectionKey)}
              </li>
            ))}
          </ul>
        </div>
        {faqItems && (
          <div className={styles.FAQContainer}>
            <Collapsible
              items={faqItems}
              alignLeft={true}
              className={styles.FAQCollapsible}
              questionClassName={styles.Question}
              answerClassName={styles.Answer}
              useQueryParams
            />
          </div>
        )}
        <div className={styles.Filler}>
          <h1>
            {t.rich('help_cta_tit', {
              Yellow: (children) => <span>{children}</span>,
            })}
          </h1>
          <p>{t('help_cta_sub_2')}</p>
          <Button additionalClasses={styles.HelpCTAButton} type="OutlineSecondary">
            {t.rich('help_cta_button')}
          </Button>
        </div>
        <div className={styles.Donation} id="guide">
          <h1>{t('help_share_tit')}</h1>
          <Spacer />
          <ul>
            <li>
              <div className={styles.DonationStepImageContainer}>
                <Image src={donationSteps[locale][0]} alt="donation-step-1" />
              </div>
              <h2>{t('help_step_1_tit')}</h2>
              <p>{t('help_step_1_bod')}</p>
            </li>
            <li>
              <div className={styles.DonationStepImageContainer}>
                <Image src={donationSteps[locale][1]} alt="donation-step-2" />
              </div>
              <h2>{t('help_step_2_tit')}</h2>
              <p>{t('help_step_2_bod')}</p>
            </li>
            <li>
              <div className={styles.DonationStepImageContainer}>
                <Image src={donationSteps[locale][2]} alt="donation-step-3" />
              </div>
              <h2>{t('help_step_3_tit')}</h2>
              <p>{t('help_step_3_bod')}</p>
            </li>
          </ul>
        </div>
      </div>
    </Page>
  )
}
export const getServerSideProps = getStaticPageProps()
